// Username rules. Kept in sync with the server validator in
// valkenhall-server/src/utils/usernameValidation.js. Both files must
// stay identical so client-side previews match server enforcement.
//
//   - 3-16 characters
//   - Must start with a letter (a-z, A-Z)
//   - Only letters, digits, and underscores after that
//   - Case-insensitive uniqueness is enforced server-side; this
//     module only validates shape (the client cannot know who else
//     has registered).
//   - Reserved words below are blocked to prevent impersonation of
//     system accounts and confusion in chat/leaderboard contexts.

export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 16;
export const USERNAME_ALLOWED_CHARS_DESCRIPTION = 'letters, numbers, and underscores';

const FULL_PATTERN = /^[A-Za-z][A-Za-z0-9_]{2,15}$/;

const RESERVED_NAMES = new Set([
  'admin', 'administrator', 'mod', 'moderator', 'staff', 'support',
  'system', 'help', 'helpdesk', 'bot', 'null', 'undefined',
  'player', 'guest', 'anonymous', 'anon', 'owner', 'root',
  'valkenhall', 'valk', 'sorcery',
]);

/**
 * Validate a username candidate. Returns either { ok: true, name } with
 * the trimmed canonical form, or { ok: false, code, message } describing
 * the first failed rule.
 *
 * Codes (stable, safe to switch on):
 *   - 'empty', 'too_short', 'too_long', 'bad_start', 'bad_chars', 'reserved'
 */
export function validateUsername(raw) {
  if (typeof raw !== 'string') {
    return { ok: false, code: 'empty', message: 'Username is required' };
  }
  const name = raw.trim();
  if (name.length === 0) {
    return { ok: false, code: 'empty', message: 'Username is required' };
  }
  if (name.length < USERNAME_MIN_LENGTH) {
    return {
      ok: false,
      code: 'too_short',
      message: `Must be at least ${USERNAME_MIN_LENGTH} characters`,
    };
  }
  if (name.length > USERNAME_MAX_LENGTH) {
    return {
      ok: false,
      code: 'too_long',
      message: `Must be at most ${USERNAME_MAX_LENGTH} characters`,
    };
  }
  if (!/^[A-Za-z]/.test(name)) {
    return {
      ok: false,
      code: 'bad_start',
      message: 'Must start with a letter',
    };
  }
  if (!FULL_PATTERN.test(name)) {
    return {
      ok: false,
      code: 'bad_chars',
      message: `Only ${USERNAME_ALLOWED_CHARS_DESCRIPTION} are allowed`,
    };
  }
  if (RESERVED_NAMES.has(name.toLowerCase())) {
    return {
      ok: false,
      code: 'reserved',
      message: 'That username is reserved. Please choose another',
    };
  }
  return { ok: true, name };
}

export function isReservedUsername(name) {
  return typeof name === 'string' && RESERVED_NAMES.has(name.trim().toLowerCase());
}

/**
 * Per-rule status used by the live checklist in the username prompt.
 * Each rule reports `met` (passes), `active` (user has typed at least
 * one char. Used to suppress green ticks on an empty field), and a
 * stable id for keying the list.
 */
export function getUsernameRuleStatus(raw) {
  const name = typeof raw === 'string' ? raw.trim() : '';
  const hasInput = name.length > 0;
  return [
    {
      id: 'length',
      label: `${USERNAME_MIN_LENGTH}–${USERNAME_MAX_LENGTH} characters`,
      met: name.length >= USERNAME_MIN_LENGTH && name.length <= USERNAME_MAX_LENGTH,
      active: hasInput,
    },
    {
      id: 'start',
      label: 'Starts with a letter',
      met: /^[A-Za-z]/.test(name),
      active: hasInput,
    },
    {
      id: 'chars',
      label: `Only ${USERNAME_ALLOWED_CHARS_DESCRIPTION}`,
      met: hasInput && /^[A-Za-z0-9_]*$/.test(name),
      active: hasInput,
    },
  ];
}
